import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, Label, Modal } from 'semantic-ui-react';

import { bem } from '../../core/design/bem';
import { useGroupProgram } from '../hooks/useGroupProgram';
import { GroupWithInvited } from '../types';
import { formatSlotRange } from '../utils';
import GroupIncompleteLabel from './GroupIncompleteLabel';
import InvitedList from './InvitedList';
import ProgramSection from './ProgramSection';

const css = bem('GroupDetailsModal');

type GroupDetailsModalProps = {
  group: GroupWithInvited;
  /** Formats de date, comme sur les autres blocs (`dateConfig`) */
  dateConfig?: Record<string, unknown>;
  onClose: () => void;
  onEdit: (group: GroupWithInvited) => void;
};

const GroupDetailsModal = ({
  group,
  dateConfig,
  onClose,
  onEdit,
}: GroupDetailsModalProps): JSX.Element => {
  const { t } = useTranslation();
  const { name, inviterName, guide, slot, invited, missingFields } = group;
  const title = name || inviterName || t('group-visits.card.untitled');
  const { demos, conferences } = useGroupProgram(group);
  const hasProgram = demos.length > 0 || conferences.length > 0;

  return (
    <Modal open onClose={onClose} closeIcon size="large" className={css().toString()}>
      <Modal.Header className={css('header').toString()}>
        <span className={css('title').toString()}>{title}</span>
        <GroupIncompleteLabel missingFields={missingFields} />
      </Modal.Header>
      <Modal.Content scrolling>
        <div className={css('meta').toString()}>
          {inviterName && (
            <Label basic>
              <Icon name="user" />
              {inviterName}
            </Label>
          )}
          {slot && (
            <Label basic>
              <Icon name="calendar alternate outline" />
              {formatSlotRange(slot)}
            </Label>
          )}
          {guide && (
            <Label basic>
              <Icon name="compass outline" />
              {guide}
            </Label>
          )}
        </div>

        <h4 className={css('subtitle').toString()}>{t('group-visits.details.program')}</h4>
        {hasProgram ? (
          <>
            <ProgramSection
              entries={demos}
              title={t('group-visits.details.demos')}
              dateConfig={dateConfig}
            />
            <ProgramSection
              entries={conferences}
              title={t('group-visits.details.conferences')}
              dateConfig={dateConfig}
            />
          </>
        ) : (
          <div className={css('empty').toString()}>{t('group-visits.details.no-program')}</div>
        )}

        <h4 className={css('subtitle').toString()}>
          {t('group-visits.details.guests', { count: invited.length })}
        </h4>
        {invited.length > 0 ? (
          <InvitedList invited={invited} />
        ) : (
          <div className={css('empty').toString()}>{t('group-visits.details.no-guest')}</div>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button primary onClick={() => onEdit(group)}>
          <Icon name="pencil" />
          {t('btn.edit')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

GroupDetailsModal.defaultProps = {
  dateConfig: undefined,
};

export default GroupDetailsModal;
